import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { ArrowRight, ChevronRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { BookFitCall } from "@/components/site/BookFitCall";
import { trackEvent } from "@/lib/analytics";
import type { ReportModel, ToolSlug } from "@/lib/tools/calc";
import { toolBySlug, toolFaqs } from "@/data/tools";
import { absoluteUrl, breadcrumbSchema, canonicalLinks, faqPageSchema, jsonLd, ogImage, ogUrl, ORG_ID, webPageSchema } from "@/lib/seo";
import { STAT_ICONS, ToolReport, toneText } from "./ToolReport";
import { ToolGate } from "./ToolGate";
import { gateIdentity, postToolLead, readGateToken } from "./gate-storage";

export const toolAppId = (slug: ToolSlug) => `${absoluteUrl(`/tools/${slug}`)}#app`;

/** head() for every tool page: meta, canonical, social, breadcrumb, app, FAQ and speakable WebPage. */
export function toolHead(slug: ToolSlug) {
  const t = toolBySlug(slug);
  return {
    meta: [
      { title: t.title },
      { name: "description", content: t.description },
      { property: "og:title", content: t.title },
      { property: "og:description", content: t.description },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: t.title },
      { name: "twitter:description", content: t.description },
      ...ogImage("tools", `${t.name} by Ajility`),
      ogUrl(t.path),
    ],
    links: canonicalLinks(t.path),
    scripts: [
      jsonLd(breadcrumbSchema([{ name: "Tools and Templates", path: "/tools" }, { name: t.name, path: t.path }])),
      jsonLd({
        ...webPageSchema({ path: t.path, name: t.title, description: t.description, mainEntityId: toolAppId(slug), image: "tools" }),
        speakable: { "@type": "SpeakableSpecification", cssSelector: ["h1", "#tool-intro"] },
      }),
      jsonLd({
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        "@id": toolAppId(slug),
        name: t.name,
        description: t.intro,
        url: absoluteUrl(t.path),
        applicationCategory: "BusinessApplication",
        operatingSystem: "Web",
        isAccessibleForFree: true,
        inLanguage: "en-AU",
        offers: { "@type": "Offer", price: "0", priceCurrency: "AUD" },
        provider: { "@id": ORG_ID },
      }),
      jsonLd(faqPageSchema(t.path, toolFaqs[slug], toolAppId(slug))),
    ],
  };
}

type ShellProps = {
  slug: ToolSlug;
  report: ReportModel;
  inputs: unknown;
  chart: ReactNode;
  form: ReactNode;
  /** Extra intro line shown above the form (Tool 3). */
  lead?: string;
};

export function ToolPageShell({ slug, report, inputs, chart, form, lead }: ShellProps) {
  const t = toolBySlug(slug);
  const faqs = toolFaqs[slug];
  const [token, setToken] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const started = useRef(false);
  const reportRef = useRef<HTMLElement>(null);
  const placement = `tool_${slug.replace(/-/g, "_")}`;

  useEffect(() => {
    setToken(readGateToken());
    trackEvent("tool_result", { source_tool: slug });
  }, [slug]);

  const markStart = () => {
    if (started.current) return;
    started.current = true;
    trackEvent("tool_start", { source_tool: slug });
  };

  const showReport = (tk: string, record: boolean) => {
    setToken(tk);
    setOpen(true);
    trackEvent("tool_report_view", { source_tool: slug });
    if (record) void postToolLead({ kind: "view", token: tk, sourceTool: slug, inputs }).catch(() => undefined);
    requestAnimationFrame(() => {
      reportRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      reportRef.current?.focus({ preventScroll: true });
    });
  };

  const who = gateIdentity(token);

  return (
    <>
      <section className="gradient-navy pb-14 pt-32 text-frost sm:pt-36 print:hidden">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <nav aria-label="Breadcrumb" className="flex flex-wrap items-center gap-1 text-xs text-frost/75">
            <Link to="/" className="hover:text-frost">Home</Link>
            <ChevronRight className="h-3 w-3" aria-hidden="true" />
            <Link to="/tools" className="hover:text-frost">Tools and Templates</Link>
            <ChevronRight className="h-3 w-3" aria-hidden="true" />
            <span aria-current="page" className="text-frost">{t.name}</span>
          </nav>
          <span className="eyebrow on-dark mt-6 block">Free tool, no login</span>
          <h1 className="mt-3 max-w-4xl font-serif text-4xl leading-tight sm:text-5xl">{t.name}</h1>
          <p id="tool-intro" className="mt-5 max-w-3xl text-lg leading-relaxed text-frost/85">{t.intro}</p>
        </div>
      </section>

      <section className="bg-surface py-12 print:hidden" aria-label={`${t.name} inputs and result`}>
        <div className="mx-auto grid max-w-7xl gap-6 px-4 sm:px-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] lg:items-start">
          <div className="card-panel min-w-0" onChangeCapture={markStart} onClickCapture={markStart}>
            <h2 className="font-serif text-2xl text-midnight">Your numbers</h2>
            {lead ? <p className="mt-2 text-charcoal">{lead}</p> : null}
            <div className="mt-5">{form}</div>
          </div>

          <div className="min-w-0 rounded-2xl bg-midnight-deep p-6 text-frost sm:p-8 lg:sticky lg:top-24 lg:max-h-[calc(100vh-7rem)] lg:overflow-y-auto">
            <span className="eyebrow on-dark">Your result</span>
            <p aria-live="polite" aria-atomic="true" className="mt-3 font-serif text-3xl leading-tight sm:text-4xl">
              {report.headline}
              {report.headlineSub ? <span className="mt-1 block font-sans text-base text-frost/80">{report.headlineSub}</span> : null}
            </p>
            <div className="mt-5 grid gap-3 sm:grid-cols-3">
              {report.stats.map((s) => {
                const Icon = STAT_ICONS[s.icon];
                return (
                  <div key={s.label} className="rounded-xl border border-white/10 bg-white/5 p-3">
                    <Icon className="h-4 w-4 text-steel-on-dark" aria-hidden="true" />
                    <p className="mt-1 text-xs text-frost/75">{s.label}</p>
                    <p className="mt-0.5 font-semibold">{s.value}</p>
                  </div>
                );
              })}
            </div>
            <p className="mt-5 text-sm leading-relaxed text-frost/90">
              <span className={`mr-2 inline-block rounded-full bg-frost px-2 py-0.5 text-xs font-bold uppercase tracking-wide ${toneText[report.tone]}`}>
                {report.tone === "red" ? "Act now" : report.tone === "amber" ? "Watch" : "On track"}
              </span>
              {report.verdict}
            </p>
            {open ? (
              <p className="mt-6 text-sm text-frost/80">Your full report is below. Change any number and it updates.</p>
            ) : token ? (
              <button type="button" onClick={() => showReport(token, true)} className="mt-6 inline-flex min-h-12 w-full items-center justify-center rounded-full bg-frost px-6 text-sm font-semibold text-midnight-deep transition hover:bg-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-frost">
                Show my full report
              </button>
            ) : (
              <ToolGate slug={slug} inputs={inputs} onUnlocked={(tk) => showReport(tk, false)} />
            )}
          </div>
        </div>
      </section>

      {open && token ? (
        <section className="tool-report-section bg-surface pb-16">
          <div className="mx-auto max-w-7xl px-4 sm:px-6">
            <ToolReport ref={reportRef} report={report} firstName={who?.firstName ?? ""} businessName={who?.businessName ?? null} chart={chart} attribution={t.attribution} />
          </div>
        </section>
      ) : null}

      <section className="bg-background py-16 print:hidden">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 sm:px-6 lg:grid-cols-2">
          <div>
            <h2 className="font-serif text-3xl text-midnight">Who this is for</h2>
            <p className="mt-4 leading-relaxed text-charcoal">
              Owners and leaders of Australian businesses between $3M and $30M in revenue who want a straight answer from their own numbers, without a spreadsheet or a sales call. It takes about {t.time}.
            </p>
          </div>
          <div>
            <h2 className="font-serif text-3xl text-midnight">How the calculation works</h2>
            {t.howItWorks.map((p) => (
              <p key={p} className="mt-4 leading-relaxed text-charcoal">{p}</p>
            ))}
            <p className="mt-4 text-sm text-charcoal">{t.attribution}</p>
          </div>
        </div>
      </section>

      <section className="bg-background pb-20 print:hidden">
        <div className="mx-auto max-w-3xl px-4 sm:px-6">
          <span className="eyebrow">FAQ</span>
          <h2 className="mt-4 font-serif text-3xl text-foreground">Common questions</h2>
          <dl className="mt-8 space-y-5">
            {faqs.map((f) => (
              <div key={f.q} className="card-panel">
                <dt><h3 className="font-serif text-lg text-foreground">{f.q}</h3></dt>
                <dd className="mt-3 text-charcoal">{f.a}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      <section className="bg-midnight-deep py-20 text-frost print:hidden">
        <div className="mx-auto max-w-4xl px-4 text-center sm:px-6">
          <h2 className="font-serif text-3xl sm:text-4xl">See the whole business, not one number</h2>
          <p className="mx-auto mt-4 max-w-2xl text-frost/85">
            The free Operating Scan is 21 plain statements about how the business actually runs, answered Not us to Always, in about six minutes.
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row sm:items-start">
            <Link to="/scan" className="inline-flex min-h-12 items-center gap-2 rounded-full bg-frost px-7 text-sm font-semibold text-midnight-deep transition hover:bg-white">
              Take the Operating Scan <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
            <BookFitCall
              placement={`${placement}_footer`}
              className="items-center sm:items-start"
              buttonClassName="inline-flex min-h-12 items-center rounded-full border border-white/25 px-7 text-sm font-semibold text-frost transition hover:border-steel-on-dark"
              noteClassName="mt-2 text-sm text-frost/75 hover:text-frost"
            />
          </div>
        </div>
      </section>
    </>
  );
}

/** Labelled numeric field used by the calculators. */
export function NumberField({ id, label, value, onChange, hint, prefix, suffix, min, max, step, placeholder }: {
  id: string;
  label: string;
  value: number | null;
  onChange: (v: number | null) => void;
  hint?: ReactNode;
  prefix?: string;
  suffix?: string;
  min?: number;
  max?: number;
  step?: number;
  placeholder?: string;
}) {
  return (
    <div>
      <label htmlFor={id} className="text-sm font-medium text-foreground">{label}</label>
      <div className="relative mt-1">
        {prefix ? <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-charcoal">{prefix}</span> : null}
        <Input
          id={id}
          type="number"
          inputMode="decimal"
          value={value ?? ""}
          min={min}
          max={max}
          step={step ?? "any"}
          placeholder={placeholder}
          aria-describedby={hint ? `${id}-hint` : undefined}
          onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
          className={`h-11 border-field-border bg-card text-foreground focus-visible:ring-2 ${prefix ? "pl-7" : ""} ${suffix ? "pr-10" : ""}`}
        />
        {suffix ? <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-charcoal">{suffix}</span> : null}
      </div>
      {hint ? <p id={`${id}-hint`} className="mt-1 text-xs text-charcoal">{hint}</p> : null}
    </div>
  );
}
